/**
 * 客服工單 (/support, boards/Support.dc.html) — support+. Inbox + ticket pane for the App's
 * one-way 「回報問題」 reports: search, status chips with counts, 只看指派給我的, status / assignee,
 * an internal-note composer (POST /api/support/tickets/:id/note — never sent to the user),
 * Email reply (mailto) and attachment downloads. `?ticket=<id>` preselects a ticket (the
 * lookup drawer links here). The reporter's SN opens the lookup drawer for admin+.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { OriginTag, StatusTag, ToneTag } from '@/components/RosterBits';
import { errMsg } from '@/components/Dialogs';
import { useAuth } from '@/contexts/AuthContext';
import { useLookup } from '@/contexts/LookupContext';
import { usePoll } from '@/lib/usePoll';
import { api } from '@/lib/api';
import { fmtDate, fmtRelative } from '@/lib/format';
import { TICKET_STATUS_LABEL, TICKET_STATUS_ORDER, ticketStatusLabel, ticketStatusTone } from '@/lib/labels';
import type { MachineLookupResult, SupportMessage, SupportTicket, SupportTicketDetail, TicketStatus } from '@/types';

// created_by_sn is the first-class column; older tickets only carry observability.machine_sn.
function ticketSn(t: Pick<SupportTicket, 'created_by_sn' | 'observability'>): string | null {
  const obs = t.observability;
  const fromObs = obs && typeof obs.machine_sn === 'string' ? obs.machine_sn : null;
  return t.created_by_sn || fromObs || null;
}

const obsStr = (t: SupportTicket, k: string): string | null => {
  const v = t.observability?.[k];
  return typeof v === 'string' && v ? v : typeof v === 'number' ? String(v) : null;
};

function reporter(t: SupportTicket): string {
  if (t.contact_email) return t.contact_email.replace(/@.*/, '@…');
  return ticketSn(t) ?? '—';
}

export function Support() {
  const { email } = useAuth();
  const [sp, setSp] = useSearchParams();
  const selectedId = sp.get('ticket');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<TicketStatus | null>(null);
  const [mine, setMine] = useState(false);

  const list = usePoll<SupportTicket[]>(() => api.listSupportTickets(), 15_000);
  const all = useMemo(
    () => [...(list.data ?? [])].sort((a, b) => (b.updated_at ?? b.created_at ?? 0) - (a.updated_at ?? a.created_at ?? 0)),
    [list.data],
  );

  const counts = useMemo(() => {
    const c: Partial<Record<TicketStatus, number>> = {};
    for (const t of all) c[t.status] = (c[t.status] ?? 0) + 1;
    return c;
  }, [all]);

  const needle = q.trim().toLowerCase();
  const visible = all.filter((t) => {
    if (status && t.status !== status) return false;
    if (mine && (!email || t.assignee !== email)) return false;
    if (!needle) return true;
    return [t.subject, t.contact_email ?? '', ticketSn(t) ?? '', t.id].some((v) => v.toLowerCase().includes(needle));
  });

  const select = (id: string) => {
    const next = new URLSearchParams(sp);
    next.set('ticket', id);
    setSp(next, { replace: true });
  };

  return (
    <div className="v2-page">
      <div className="v2-head">
        <div className="titles">
          <h1 className="v2-title">客服工單</h1>
          <p className="v2-sub">App 的「回報問題」送來的單向回報 · 改狀態、指派、以 Email 回覆、下載附件</p>
        </div>
      </div>

      <div className="support2">
        <section className="panel flush inbox">
          <div className="inbox-tools">
            <label className="searchbox">
              <Icon name="search" size={14} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="主旨、Email、機器序號" aria-label="搜尋工單" />
            </label>
            <div className="pills">
              <button type="button" className={'pill sm' + (status === null ? ' on' : '')} onClick={() => setStatus(null)}>全部</button>
              {TICKET_STATUS_ORDER.map((s) => (
                <button key={s} type="button" className={'pill sm' + (status === s ? ' on' : '')} onClick={() => setStatus(status === s ? null : s)}>
                  {TICKET_STATUS_LABEL[s]}{counts[s] && s !== 'resolved' && s !== 'closed' ? ` ${counts[s]}` : ''}
                </button>
              ))}
            </div>
            <label className="opt-row" style={{ fontSize: 'var(--text-caption)' }}>
              <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} disabled={!email} />
              只看指派給我的
            </label>
          </div>
          <div className="inbox-list">
            {Boolean(list.error) && !list.data && <div className="error-line" style={{ padding: 'var(--space-3)' }}>載入失敗:{errMsg(list.error)}</div>}
            {visible.length === 0 ? (
              <div style={{ padding: 'var(--space-5)' }} className="muted-note">{list.loading && !list.data ? '載入中…' : '沒有符合的工單'}</div>
            ) : (
              visible.map((t) => (
                <button key={t.id} type="button" className={'inbox-item' + (t.id === selectedId ? ' on' : '')} onClick={() => select(t.id)}>
                  <span className="r1">
                    <span className="subj">{t.subject}</span>
                    <span className="at">{fmtRelative(t.updated_at ?? t.created_at)}</span>
                  </span>
                  <span className="r2">
                    <ToneTag tone={ticketStatusTone(t.status)}>{ticketStatusLabel(t.status)}</ToneTag>
                    <span className="grow">#{t.id.slice(0, 8)} · {reporter(t)}</span>
                    <span>{t.message_count} 則</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </section>

        <section className="panel flush ticket-pane">
          {selectedId ? (
            <TicketPane key={selectedId} ticketId={selectedId} myEmail={email} onMutated={list.refresh} />
          ) : (
            <div style={{ margin: 'auto', padding: 'var(--space-6)' }}>
              <EmptyState title="選擇一張工單" description="從左側清單點選一張工單以查看內容。" />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const WHO: Record<SupportMessage['author_type'], (m: SupportMessage) => string> = {
  user: () => '使用者',
  internal_note: (m) => `內部備註 · ${m.author_id ? m.author_id.replace(/@.*/, '@…') : '—'}`,
  admin: () => '客服回覆(經 Email 寄出)',
};

function TicketPane({ ticketId, myEmail, onMutated }: { ticketId: string; myEmail: string | null; onMutated: () => void }) {
  const lookup = useLookup();
  const { data, error, loading, refresh } = usePoll<SupportTicketDetail>(() => api.getSupportTicket(ticketId), 10_000, [ticketId]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const threadRef = useRef<HTMLDivElement | null>(null);

  const ticket = data?.ticket ?? null;
  const messages = data?.messages ?? [];
  const sn = ticket ? ticketSn(ticket) : null;

  // The reporter's roster state (admin+ only; the lookup endpoint is admin-gated).
  // Same normalization as the drawer: the Worker only takes [A-Z0-9-]{4,64}.
  const lookupQ = (sn ?? '').toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 64);
  // The answer is kept WITH the query it answers, so a reply for the previous ticket never shows here.
  const [lookupRes, setLookupRes] = useState<{ q: string; r: MachineLookupResult } | null>(null);
  useEffect(() => {
    if (!lookup.enabled || lookupQ.length < 4) return;
    let alive = true;
    api.machineLookup(lookupQ).then((r) => { if (alive) setLookupRes({ q: lookupQ, r }); }).catch(() => undefined);
    return () => { alive = false; };
  }, [lookup.enabled, lookupQ, lookup.changeTick]);
  const machine = lookupRes && lookupRes.q === lookupQ ? lookupRes.r : null;
  // Only an EXACT SN match is this reporter's machine — a suffix lookup can pick a different SN.
  const exactEntry = machine?.machine?.entry.sn === lookupQ ? machine.machine.entry : null;

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const revalidate = useCallback(() => { refresh(); onMutated(); }, [refresh, onMutated]);
  const assignedToMe = !!myEmail && ticket?.assignee === myEmail;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); revalidate(); return true; }
    catch (e) { setErr(errMsg(e)); return false; }
    finally { setBusy(false); }
  };

  if (!ticket) {
    return (
      <div style={{ margin: 'auto', padding: 'var(--space-5)' }}>
        {loading ? <span className="muted">載入中…</span> : <EmptyState title="無法載入工單" description={error instanceof Error ? error.message : '請稍後再試,或重新選擇工單。'} />}
      </div>
    );
  }

  const appLine = [obsStr(ticket, 'app_version'), obsStr(ticket, 'os')].filter(Boolean).join(' · ') || '—';
  const ram = obsStr(ticket, 'ram_total_gb');
  const gpuLine = [obsStr(ticket, 'gpu'), ram ? `RAM ${ram} GB` : null].filter(Boolean).join(' · ') || '—';
  const entry = exactEntry;

  return (
    <>
      <div className="ticket-head">
        <div className="r1">
          <h2>{ticket.subject}</h2>
          <label className="inline-field" style={{ fontSize: 'var(--text-caption)' }}>狀態
            <select className="v2-select" value={ticket.status} disabled={busy} onChange={(e) => void run(() => api.setSupportTicketStatus(ticketId, e.target.value as TicketStatus))}>
              {TICKET_STATUS_ORDER.map((s) => <option key={s} value={s}>{TICKET_STATUS_LABEL[s]}</option>)}
            </select>
          </label>
          <Button className="btn-outline" disabled={!myEmail || busy} onClick={() => void run(() => api.setSupportTicketAssignee(ticketId, assignedToMe ? '' : (myEmail ?? '')))}>
            {assignedToMe ? '取消指派' : '指派給我'}
          </Button>
        </div>
        {ticket.assignee && !assignedToMe && <span className="muted-note">指派給 {ticket.assignee}</span>}
        <div className="facts">
          <div className="fact">
            <div className="k">回報者機器</div>
            {sn ? (
              lookup.enabled
                ? <button type="button" className="link mono" onClick={() => lookup.open(sn)}>{sn}</button>
                : <span className="mono">{sn}</span>
            ) : '—'}
            {entry && (
              <div className="row" style={{ gap: 4, marginTop: 4 }}>
                <StatusTag status={entry.status} /><OriginTag origin={entry.origin} />
              </div>
            )}
            {lookup.enabled && sn && machine && !entry && <div className="muted-note">不在名單上</div>}
          </div>
          <div className="fact">
            <div className="k">聯絡信箱</div>
            {ticket.contact_email ? <a className="link" href={`mailto:${ticket.contact_email}`}>{ticket.contact_email}</a> : '未留'}
          </div>
          <div className="fact"><div className="k">App 版本 / 系統</div>{appLine}</div>
          <div className="fact"><div className="k">顯示卡</div>{gpuLine}</div>
        </div>
      </div>

      <div className="thread" ref={threadRef}>
        {messages.length === 0 ? (
          <span className="muted">尚無訊息。</span>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={'bubble ' + m.author_type}>
              <span className="who">{WHO[m.author_type]?.(m) ?? m.author_type} · {fmtDate(m.created_at)}</span>
              <span className="txt">{m.text}</span>
              {m.attachment_refs && m.attachment_refs.length > 0 && (
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {m.attachment_refs.map((path) => {
                    const name = path.split('/').pop() || '附件';
                    return (
                      <a key={path} className="attachment-chip" href={api.supportAttachmentUrl(path)} download={name} title={`下載 ${name}`}>
                        <Icon name="paperclip" size={14} />
                        <span className="attachment-chip__name">{name}</span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {err && <div className="error-line" role="alert" style={{ padding: '0 var(--space-5) var(--space-2)' }}>{err}</div>}
      <div className="composer">
        <label className="v2-field">內部備註(使用者看不到)
          <textarea className="v2-textarea" rows={2} maxLength={8000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="記錄調查進度或交接事項" />
        </label>
        <Button
          className="btn-outline"
          disabled={busy || !note.trim()}
          onClick={async () => { if (await run(() => api.addSupportNote(ticketId, note.trim()))) setNote(''); }}
        >
          加備註
        </Button>
        {ticket.contact_email ? (
          <a className="btn btn-primary" href={`mailto:${ticket.contact_email}?subject=${encodeURIComponent('Re: ' + ticket.subject)}`}>以 Email 回覆</a>
        ) : (
          <Button variant="primary" disabled title="此使用者未留聯絡信箱">以 Email 回覆</Button>
        )}
      </div>
    </>
  );
}
